import { useGetUserNameWithFallback } from '@pba/react-utils';
import type { CommunityDetail } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Modal, message } from 'ui';
import { useDeleteUserFromCommunityMutation } from '../../../queries';
import type { User } from '../../user-list';

type P = {
  community: CommunityDetail;
};

export const useDeleteMember = ({ community }: P) => {
  const { t } = useTranslation();

  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync: deleteUser } = useDeleteUserFromCommunityMutation();
  const getUserNameWithFallback = useGetUserNameWithFallback();

  const confirm = useCallback(
    (user: User) => {
      modal.confirm({
        content: 'This action is irreversible.',
        icon: <Icon color="error" icon="delete-2-line" />,
        okText: 'Yes, remove',
        async onOk() {
          try {
            await deleteUser(
              {
                communityId: community.id,
                userId: user.id,
              },
              {
                onError() {
                  message.error('Something went wrong. The user could’t be removed.');
                },
                onSuccess() {
                  message.success('User removed successfully');
                },
              },
            );
          } catch {}
        },
        title: t('Remove user from?', {
          fullName: getUserNameWithFallback(user),
          groupName: community.name,
        }),
        type: 'error',
      });
    },
    [community.id, community.name, deleteUser, getUserNameWithFallback, modal, t],
  );

  return useMemo(() => ({ confirm, contextHolder }), [confirm, contextHolder]);
};
